/* eslint-disable @bigbinary/neeto/use-neetoui-classes */
import { Separator } from "@bigbinary/neeto-atoms/primitives";
import classnames from "classnames";
import { Crown } from "lucide-react";
import { isPresent, humanize, isNotEmpty } from "neetocist";
import { useTranslation } from "react-i18next";

import HelpMenu from "./HelpMenu";
import LinkSection from "./LinkSection";
import OrganizationSwitcher from "./OrganizationSwitcher";
import ProductSwitcherMenu from "./ProductSwitcher";
import ProfileExpandMenu from "./ProfileExpandMenu";
import ThemeSwitcher from "./ThemeSwitcher";
import WorkspaceMenu from "./WorkspaceMenu";

import { NEETO_AUTH_BILLING_INFO_URL } from "../constants";

const ProfileMenu = ({
  profileInfo,
  showProductSwitcher,
  defaultLinks,
  topLinks,
  bottomLinks,
  customContent,
  toggleModal,
  isThemeSwitcherEnabled,
  isOrganizationSwitcherEnabled,
  isConsumer,
  enableSubscriptionUpgradeRequest,
  openUpgradeModal,
}) => {
  const { isAuthenticated } = profileInfo;
  const { t } = useTranslation();

  const normalizedPlan = (
    globalProps.user?.subscriptionPlan || ""
  ).toLowerCase();

  const isFreePlan = normalizedPlan === "free";
  const isOwner = globalProps.user?.isOwner;
  const showUpgradeModal =
    isFreePlan && enableSubscriptionUpgradeRequest && !isOwner;

  const getSubscriptionPlanAction = () => {
    if (showUpgradeModal) return { onClick: openUpgradeModal };

    if (isFreePlan && isOwner && enableSubscriptionUpgradeRequest) {
      return {
        href: `${globalProps.organization?.authAppUrl}/admin/billing/overview`,
      };
    }

    return { href: NEETO_AUTH_BILLING_INFO_URL };
  };

  const subscriptionPlanLink = {
    label: (
      <>
        {globalProps.appName}{" "}
        <span
          className={classnames(
            "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] leading-tight font-semibold",
            {
              "bg-[#FFE44A] text-black": normalizedPlan === "pro",
              "bg-primary text-primary-foreground": isFreePlan,
            }
          )}
        >
          {normalizedPlan === "pro" ? (
            humanize(globalProps.user?.subscriptionPlan)
          ) : (
            <>
              <Crown size={12} />
              {t("neetoMolecules.sidebar.upgradePlan")}
            </>
          )}
        </span>
      </>
    ),
    "data-testid": "subscription-plan",
    ...getSubscriptionPlanAction(),
  };

  return (
    <div className="p-1.5">
      {!isConsumer && (
        <>
          <LinkSection links={defaultLinks} />
          {isNotEmpty(topLinks) && (
            <>
              <Separator className="my-2" />
              <LinkSection links={topLinks} />
            </>
          )}
          {isPresent(customContent) && (
            <>
              <Separator className="my-2" />
              <div className="px-3 py-1">{customContent}</div>
            </>
          )}
          <Separator className="my-2" />
          {globalProps.user?.isOwner && <WorkspaceMenu />}
          {isOrganizationSwitcherEnabled && <OrganizationSwitcher />}
          <Separator className="my-2" />
          {isPresent(globalProps.user?.subscriptionPlan) && (
            <LinkSection links={[subscriptionPlanLink]} />
          )}
          {showProductSwitcher && <ProductSwitcherMenu {...{ toggleModal }} />}
          <HelpMenu />
          <Separator className="my-2" />
        </>
      )}
      {isAuthenticated && !isConsumer && (
        <ProfileExpandMenu {...{ profileInfo }} />
      )}
      <LinkSection links={bottomLinks} />
      {isThemeSwitcherEnabled && (
        <>
          <Separator className="my-2" />
          <ThemeSwitcher />
        </>
      )}
    </div>
  );
};

export default ProfileMenu;
